import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../../components/Spinner';
import 'react-quill/dist/quill.snow.css'; // Gerekli olmasa da bazen stil çakışmalarını önler

const AdminQuizEditPage = () => {
    const { id } = useParams(); // URL'den quiz ID'sini al
    const [quiz, setQuiz] = useState(null); // Düzenlenecek quiz'in state'i
    const [courses, setCourses] = useState([]); // Kurs listesi state'i
    const [loading, setLoading] = useState(true); // Sayfa yükleme durumu
    const [submitting, setSubmitting] = useState(false); // Form gönderme durumu
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);

    // Quiz ve kurs bilgilerini çekmek için useEffect
    useEffect(() => {
        const fetchData = async () => {
            if (!token || !id) return;
            setLoading(true);
            try {
                const config = { headers: { 'x-auth-token': token } };
                // Admin detay rotasını kullanarak quiz'in tam bilgisini çekiyoruz (doğru cevaplar dahil)
                const quizRes = await axios.get(`${API_BASE_URL}/api/quizzes/details/${id}`, config);
                // Tüm kursları çekiyoruz (select menüsü için)
                const coursesRes = await axios.get(`${API_BASE_URL}/api/courses`, config);

                setQuiz(quizRes.data);
                setCourses(coursesRes.data);
            } catch (err) {
                toast.error('Quiz veya kurs verileri getirilemedi.');
                navigate('/admin/quizzes'); // Hata olursa listeye geri dön
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, token, navigate]);

    // Quiz genel bilgilerini (başlık, kurs, geçme notu) güncelleyen fonksiyon
    const handleQuizInfoChange = (e) => {
        setQuiz({ ...quiz, [e.target.name]: e.target.value });
    };

    // Belirli bir sorunun metnini güncelleyen fonksiyon
    const handleQuestionChange = (index, e) => {
        const updatedQuestions = [...quiz.questions];
        updatedQuestions[index][e.target.name] = e.target.value;
        setQuiz({ ...quiz, questions: updatedQuestions });
    };

    // Belirli bir sorunun doğru cevabını güncelleyen fonksiyon
    const handleCorrectAnswerChange = (qIndex, value) => {
        const updatedQuestions = [...quiz.questions];
        updatedQuestions[qIndex].correctAnswer = value;
        setQuiz({ ...quiz, questions: updatedQuestions });
    };

    // Belirli bir sorunun belirli bir seçeneğini güncelleyen fonksiyon
    const handleOptionChange = (qIndex, oIndex, e) => {
        const updatedQuestions = [...quiz.questions];
        // Eğer options dizisi o index'e kadar dolu değilse, boş stringlerle doldur
        while (updatedQuestions[qIndex].options.length <= oIndex) {
            updatedQuestions[qIndex].options.push('');
        }
        updatedQuestions[qIndex].options[oIndex] = e.target.value;
        setQuiz({ ...quiz, questions: updatedQuestions });
    };

    // Yeni bir boş soru ekleyen fonksiyon
    const addQuestion = () => {
        setQuiz({
            ...quiz,
            questions: [...quiz.questions, { questionText: '', options: ['', '', '', ''], correctAnswer: '' }]
        });
    };

    // Belirli bir soruyu silen fonksiyon
    const removeQuestion = (index) => {
        if (quiz.questions.length <= 1) {
            return toast.error('Quizde en az bir soru olmalıdır.');
        }
        const updatedQuestions = quiz.questions.filter((_, qIndex) => qIndex !== index);
        // Doğru cevap silinen seçeneklerden biriyse, correctAnswer'ı sıfırla
        updatedQuestions.forEach(q => {
            if (!q.options.includes(q.correctAnswer)) {
                q.correctAnswer = '';
            }
        });
        setQuiz({ ...quiz, questions: updatedQuestions });
    };

    // Formu backend'e gönderen fonksiyon
    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const loadingToast = toast.loading('Quiz güncelleniyor...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            // Göndermeden önce boş seçenekleri temizle
            const payload = {
                ...quiz,
                questions: quiz.questions.map(q => ({
                    ...q,
                    options: q.options.filter(opt => opt && opt.trim() !== '')
                }))
            };
             // Her sorunun en az 2 seçeneği ve bir doğru cevabı olduğundan emin ol
             const validationError = payload.questions.some(q => q.options.length < 2 || !q.correctAnswer || !q.options.includes(q.correctAnswer));
             if (validationError) {
                 toast.error('Her sorunun en az 2 seçeneği olmalı ve doğru cevap işaretlenmelidir.', { id: loadingToast });
                 setSubmitting(false);
                 return;
             }

            await axios.put(`${API_BASE_URL}/api/quizzes/${id}`, payload, config);
            toast.success('Quiz başarıyla güncellendi!', { id: loadingToast });
            navigate('/admin/quizzes');
        } catch (err) {
            console.error(err.response);
            toast.error(err.response?.data?.msg || 'Quiz güncellenemedi.', { id: loadingToast });
            setSubmitting(false);
        }
    };

    if (loading) return <Spinner />;
    if (!quiz) return <div className="text-center card p-8">Failed to load quiz information.</div>;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold">Edit Quiz</h1>
                <Link to="/admin/quizzes" className="btn btn-secondary">
                    ← Back to Quiz Management
                </Link>
            </div>
            <form onSubmit={onSubmit} className="space-y-6">
                {/* Quiz General Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input type="text" name="title" placeholder="Quiz Title" value={quiz.title} onChange={handleQuizInfoChange} required className="input-field md:col-span-2" />
                    <input type="number" name="passingScore" placeholder="Passing Score (%)" value={quiz.passingScore} onChange={handleQuizInfoChange} required className="input-field" />
                </div>
                <select name="courseId" value={quiz.courseId} onChange={handleQuizInfoChange} required className="input-field">
                    <option value="">Select a Course</option>
                    {courses.map(course => <option key={course._id} value={course._id}>{course.title}</option>)}
                </select>

                {/* Questions Section */}
                <div className="space-y-4">
                    {quiz.questions.map((q, qIndex) => (
                        <div key={qIndex} className="p-4 border rounded-md bg-light-bg dark:bg-dark-bg">
                            <div className="flex justify-between items-center mb-2">
                                <label className="font-semibold">Question {qIndex + 1}</label>
                                {quiz.questions.length > 1 && (
                                    <button type="button" onClick={() => removeQuestion(qIndex)} className="text-red-500 text-sm hover:underline">Delete Question</button>
                                )}
                            </div>
                            <textarea name="questionText" placeholder="Write your question here..." value={q.questionText} onChange={(e) => handleQuestionChange(qIndex, e)} required className="input-field"></textarea>
                            <p className="text-sm mt-4 mb-2 opacity-70">Options (Check the correct one):</p>
                            {(q.options || ['', '', '', '']).map((option, oIndex) => ( // options yoksa diye varsayılan ekledik
                                <div key={oIndex} className="flex items-center gap-2 mt-2">
                                    <input
                                        type="radio"
                                        name={`correctAnswer-${qIndex}`}
                                        value={option} // Değerin boş olmaması önemli
                                        checked={q.correctAnswer === option && option !== ''} // Boş string'in seçili olmasını engelle
                                        onChange={() => handleCorrectAnswerChange(qIndex, option)}
                                        required
                                        className="form-radio h-4 w-4 text-light-primary dark:text-dark-primary"
                                    />
                                    <input
                                        type="text"
                                        placeholder={`Option ${oIndex + 1}`}
                                        value={option}
                                        onChange={(e) => handleOptionChange(qIndex, oIndex, e)}
                                        className="input-field"
                                    />
                                </div>
                            ))}
                            {/* Seçenek sayısını 4'e tamamlamak için (görsel tutarlılık) */}
                            {Array.from({ length: Math.max(0, 4 - (q.options?.length || 0)) }).map((_, fakeIndex) => {
                                const oIndex = (q.options?.length || 0) + fakeIndex;
                                return (
                                <div key={`fake-${oIndex}`} className="flex items-center gap-2 mt-2">
                                    <input type="radio" name={`correctAnswer-${qIndex}`} disabled className="opacity-0"/> {/* Hidden radio */}
                                    <input type="text" placeholder={`Option ${oIndex + 1}`} value={''} onChange={(e) => handleOptionChange(qIndex, oIndex, e)} className="input-field"/>
                                </div>
                                )
                             })}
                        </div>
                    ))}
                </div>
                {/* /Questions Section */}

                <button type="button" onClick={addQuestion} className="w-full py-2 border-dashed border-2 rounded-md hover:bg-light-bg dark:hover:bg-dark-border transition-colors">
                    + Add New Question
                </button>
                <button type="submit" disabled={submitting} className="btn-primary w-full">
                     {submitting ? <Spinner size='h-5 w-5 border-b-2' /> : 'Save Changes'}
                </button>
            </form>
        </motion.div>
    );
};

export default AdminQuizEditPage;